import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {WithRouterProps} from 'react-router';

import {addToast, LoadingSpinner} from '@albert-io/atomic';
import type {SubjectModelV2} from '@albert-io/models';

import {
  getIndiePracticeSubjectsQuery,
  getClassroomIndiePracticeSubjectsQuery
} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

import useOnResourceStale from '../hooks/useOnResourceStale';
import {StudentIndependentPracticeCard} from './StudentIndependentPracticeCard';
import {StudentIndependentPracticeDropdown} from './StudentIndependentPracticeDropdown';
import {Subject, CurriculumArea, FilterOption} from './StudentIndependentPractice.types';
import {kebabCase} from 'lodash';
import TabEmptyState from '../TabEmptyState';

const subjectCache = new Map<string | undefined, SubjectModelV2[]>();

function mapSubjectModel(subject: SubjectModelV2): Subject {
  const curriculumAreaModel = subject.getCurriculumArea();
  const curriculumArea: CurriculumArea = {
    id: curriculumAreaModel.getId(),
    name: curriculumAreaModel.getName(),
    slug: kebabCase(curriculumAreaModel.getName())
  };
  return {
    id: subject.getId(),
    name: subject.getName(),
    urlSlug: subject.getUrlSlug(),
    logoUrl: subject.getLogoLocation().href ?? '',
    isAdaptive: subject.isAdaptive(),
    curriculumArea
  };
}

function extractCurriculumAreas(subjectList: SubjectModelV2[]): CurriculumArea[] {
  const curriculumAreaIds = new Set<string>();
  const uniqueCurriculumAreas: CurriculumArea[] = [];
  subjectList.forEach((s) => {
    const ca = s.getCurriculumArea();
    if (!ca.isEmpty() && !curriculumAreaIds.has(ca.getId())) {
      curriculumAreaIds.add(ca.getId());
      uniqueCurriculumAreas.push({
        id: ca.getId(),
        name: ca.getName(),
        slug: kebabCase(ca.getName())
      });
    }
  });
  return uniqueCurriculumAreas;
}

export function StudentIndependentPractice({params}: WithRouterProps) {
  const classroomId: string | undefined = params?.classroomId;
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');

  const [activeSubjects, setActiveSubjects] = useState<SubjectModelV2[] | null>(
    subjectCache.get(params?.classroomId) ?? null
  );

  const {
    updateIndependentPracticeSubjectsLastFetchedAt,
    studentLastFetchedAt,
    independentPracticeSubjectsLastFetchedAt
  } = useStudentDashboard(
    useShallow((state) => ({
      updateIndependentPracticeSubjectsLastFetchedAt:
        state.updateIndependentPracticeSubjectsLastFetchedAt,
      studentLastFetchedAt: state.studentLastFetchedAt,
      independentPracticeSubjectsLastFetchedAt: state.independentPracticeSubjectsLastFetchedAt
    }))
  );

  // When the student is re-fetched, only invalidate and clear data when on the home page.
  const classroomIdRef = useRef(classroomId);
  classroomIdRef.current = classroomId;

  const handleStale = useCallback(() => {
    if (classroomIdRef.current) return;

    getIndiePracticeSubjectsQuery().invalidateInterest();
    subjectCache.clear();
    updateIndependentPracticeSubjectsLastFetchedAt();
    setActiveSubjects(null);
  }, [updateIndependentPracticeSubjectsLastFetchedAt]);

  useOnResourceStale(independentPracticeSubjectsLastFetchedAt, handleStale);

  useEffect(() => {
    let cancelled = false;

    // Already served from cache during render
    if (subjectCache.has(classroomId)) return;

    const query = classroomId
      ? getClassroomIndiePracticeSubjectsQuery(classroomId)
      : getIndiePracticeSubjectsQuery();

    query
      .getResourcePromise()
      .then((result: any) => {
        const subjects = result.toArray();
        subjectCache.set(classroomId, subjects);
        if (!cancelled) {
          setActiveSubjects(subjects);
          updateIndependentPracticeSubjectsLastFetchedAt();
        }
      })
      .catch(() => {
        addToast({
          color: 'negative',
          message: 'Failed to load independent practice subjects.'
        });
      });

    return () => {
      cancelled = true;
    };
  }, [classroomId, studentLastFetchedAt, updateIndependentPracticeSubjectsLastFetchedAt]);

  const subjectModels = useMemo<SubjectModelV2[]>(() => {
    return (activeSubjects ?? []).filter((s: SubjectModelV2) => !s.isAdaptive());
  }, [activeSubjects]);

  const isLoading = activeSubjects === null;

  const curriculumAreas = useMemo(() => extractCurriculumAreas(subjectModels), [subjectModels]);

  const filteredSubjects = useMemo(() => {
    const mapped = subjectModels.map(mapSubjectModel);
    return selectedFilter === 'All'
      ? mapped
      : mapped.filter((s) => s.curriculumArea.name === selectedFilter);
  }, [subjectModels, selectedFilter]);

  function renderContent() {
    if (isLoading) {
      return <LoadingSpinner bottomMarginLevel={0} size={3} />;
    }

    if (filteredSubjects.length === 0) {
      return <TabEmptyState copy='No subjects available for practice.' icon={['fal', 'books']} />;
    }

    return (
      <div className='grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-x1'>
        {filteredSubjects.map((subject) => (
          <StudentIndependentPracticeCard subject={subject} key={subject.id} />
        ))}
      </div>
    );
  }

  return (
    <div className='w-full px-x2 py-x3 m:px-x4'>
      <div className='mb-x3'>
        <StudentIndependentPracticeDropdown
          curriculumAreas={curriculumAreas}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      </div>

      {renderContent()}
    </div>
  );
}
